import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileDown, FileSpreadsheet, Search, Users, Clock, CalendarOff, Palmtree } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (s) => s ? s.split('-').reverse().join('/') : '';
const sign = (n) => `${n >= 0 ? '+' : ''}${n}`;

const HEAD = ['Empleado', 'Tienda', 'H/sem', 'Trabajadas', 'Previstas', 'Dif.', 'Días trab.', 'Descansos', 'Festivos', 'Vacaciones', 'Días libres', 'Pend.'];
const rowCells = (r) => [r.name + (r.position ? ` (${r.position})` : ''), r.store_name || '-', r.weekly_hours || 0, r.worked, r.expected, sign(r.diff), r.worked_days, r.rest_days, r.holidays, r.vacation_days, r.free_days, r.pending_requests];
const totalCells = (t) => ['TOTAL PLANTILLA', `${t.employees} empleados`, '', t.worked, t.expected, sign(t.diff), t.worked_days, t.rest_days, t.holidays, t.vacation_days, t.free_days, t.pending_requests];

function StatCard({ icon: Icon, label, value, sub, color = 'text-slate-900', testId }) {
  return (
    <Card className="border-slate-200"><CardContent className="p-4 flex items-start justify-between">
      <div>
        <p className="text-xs uppercase font-bold text-slate-400">{label}</p>
        <p className={`text-2xl font-bold font-['Barlow_Condensed'] ${color}`} data-testid={testId}>{value}</p>
        {sub && <p className="text-xs text-slate-500">{sub}</p>}
      </div>
      <Icon size={20} className="text-slate-300" />
    </CardContent></Card>
  );
}

export default function PersonalInformeGlobal({ periodRange }) {
  const [period, setPeriod] = useState('mes');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = useCallback(async () => {
    const range = period === 'rango' ? { from: customFrom, to: customTo } : periodRange(period);
    if (!range?.from || !range?.to) { toast.error('Selecciona el rango de fechas'); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/personal/reports/global`, { params: { date_from: range.from, date_to: range.to, include_inactive: includeInactive } });
      setReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar el informe global');
    } finally {
      setLoading(false);
    }
  }, [period, customFrom, customTo, includeInactive, periodRange]);

  const title = report ? `Informe global de plantilla · ${fmt(report.date_from)} — ${fmt(report.date_to)}` : '';
  const fileBase = report ? `informe_plantilla_${report.date_from}_${report.date_to}` : '';

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFontSize(16); doc.setTextColor(15, 23, 42);
    doc.text('Informe global de plantilla — Personal', 14, 16);
    doc.setFontSize(10); doc.setTextColor(100);
    doc.text(`Periodo: ${fmt(report.date_from)} — ${fmt(report.date_to)} · ${report.totals.employees} empleados · ${report.days_in_range} días`, 14, 23);
    autoTable(doc, {
      startY: 30, head: [HEAD], body: report.rows.map(rowCells), foot: [totalCells(report.totals)],
      styles: { fontSize: 8, cellPadding: 1.5 }, headStyles: { fillColor: [15, 23, 42] },
      footStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold' },
    });
    doc.save(`${fileBase}.pdf`);
  };

  const exportExcel = () => {
    const data = [['Informe global de plantilla — Personal'], [`Periodo: ${fmt(report.date_from)} — ${fmt(report.date_to)}`], [], HEAD, ...report.rows.map(rowCells), [], totalCells(report.totals)];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(data), 'Plantilla');
    XLSX.writeFile(wb, `${fileBase}.xlsx`);
  };

  const printReport = () => {
    const win = window.open('', '_blank');
    const tr = (cells, tag = 'td') => `<tr>${cells.map((c, i) => `<${tag} style="${i >= 2 ? 'text-align:right;' : ''}">${c}</${tag}>`).join('')}</tr>`;
    win.document.write(`<html><head><title>Informe plantilla</title><style>
      body{font-family:Arial,sans-serif;padding:20px;color:#0f172a} h1{font-size:18px;margin:0} p{color:#64748b;margin:4px 0;font-size:13px}
      table{width:100%;border-collapse:collapse;margin-top:14px;font-size:12px} th,td{border:1px solid #e2e8f0;padding:5px 7px;text-align:left}
      th{background:#0f172a;color:#fff} tfoot td{font-weight:bold;background:#f1f5f9} @media print{@page{size:landscape;margin:12mm}}
    </style></head><body><h1>Informe global de plantilla — Personal</h1><p>${title}</p>
      <table><thead>${tr(HEAD, 'th')}</thead><tbody>${report.rows.map(r => tr(rowCells(r))).join('')}</tbody><tfoot>${tr(totalCells(report.totals))}</tfoot></table>
    </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 300);
  };

  return (
    <div className="space-y-4" data-testid="global-report">
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label>Periodo</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40" data-testid="global-report-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="semana">Esta semana</SelectItem>
                <SelectItem value="mes">Este mes</SelectItem>
                <SelectItem value="anio">Este año</SelectItem>
                <SelectItem value="rango">Rango de fechas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {period === 'rango' && (
            <>
              <div className="space-y-1.5"><Label>Desde</Label><Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="global-report-from" /></div>
              <div className="space-y-1.5"><Label>Hasta</Label><Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="global-report-to" /></div>
            </>
          )}
          <label className="flex items-center gap-2 pb-2.5 text-sm text-slate-600 cursor-pointer">
            <Checkbox checked={includeInactive} onCheckedChange={(v) => setIncludeInactive(!!v)} data-testid="global-report-inactive" /> Incluir bajas/inactivos
          </label>
          <Button onClick={generate} disabled={loading} className="bg-orange-500 hover:bg-orange-600" data-testid="generate-global-report-btn">
            <Search size={16} className="mr-2" />{loading ? 'Generando...' : 'Generar'}
          </Button>
        </CardContent>
      </Card>

      {report && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="global-report-totals">
            <StatCard icon={Users} label="Empleados" value={report.totals.employees} sub={`${report.days_in_range} días en el periodo`} testId="global-total-employees" />
            <StatCard icon={Clock} label="Horas trabajadas" value={`${report.totals.worked}h`} sub={`Previstas: ${report.totals.expected}h`} testId="global-total-worked" />
            <StatCard icon={Clock} label="Diferencia" value={`${sign(report.totals.diff)}h`} color={report.totals.diff >= 0 ? 'text-green-600' : 'text-red-600'} testId="global-total-diff" />
            <StatCard icon={CalendarOff} label="Descansos + festivos" value={report.totals.rest_days + report.totals.holidays} sub={`${report.totals.rest_days} descansos · ${report.totals.holidays} festivos`} testId="global-total-rest" />
            <StatCard icon={Palmtree} label="Ausencias" value={report.totals.absence_days} sub={`${report.totals.vacation_days} vacaciones · ${report.totals.free_days} días libres · ${report.totals.pending_requests} pend.`} color="text-blue-600" testId="global-total-absences" />
          </div>

          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">{title}</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={exportPDF} data-testid="global-export-pdf-btn"><FileDown size={16} className="mr-2" />PDF</Button>
                <Button variant="outline" size="sm" onClick={exportExcel} data-testid="global-export-excel-btn"><FileSpreadsheet size={16} className="mr-2" />Excel</Button>
                <Button variant="outline" size="sm" onClick={printReport} data-testid="global-print-btn">Imprimir</Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="global-report-table">
                  <thead>
                    <tr className="bg-slate-50 text-xs uppercase text-slate-500">
                      <th className="text-left p-2.5">Empleado</th>
                      <th className="text-left p-2.5">Tienda</th>
                      <th className="text-right p-2.5">H/sem</th>
                      <th className="text-right p-2.5">Trab.</th>
                      <th className="text-right p-2.5">Prev.</th>
                      <th className="text-right p-2.5">Dif.</th>
                      <th className="text-right p-2.5">Días trab.</th>
                      <th className="text-right p-2.5">Descansos</th>
                      <th className="text-right p-2.5">Festivos</th>
                      <th className="text-right p-2.5">Vacac.</th>
                      <th className="text-right p-2.5">D. libres</th>
                      <th className="text-right p-2.5">Pend.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.length === 0 && (
                      <tr><td colSpan={12} className="p-6 text-center text-slate-400">No hay empleados para este filtro</td></tr>
                    )}
                    {report.rows.map(r => (
                      <tr key={r.employee_id} className="border-t border-slate-100" data-testid={`global-row-${r.employee_id}`}>
                        <td className="p-2.5">
                          <span className="font-semibold text-slate-800">{r.name}</span>
                          {r.position && <span className="text-xs text-slate-500 ml-1">· {r.position}</span>}
                          {r.status !== 'activo' && <Badge variant="outline" className="ml-2 text-[10px] text-slate-500">{r.status}</Badge>}
                        </td>
                        <td className="p-2.5 text-slate-500">{r.store_name || '—'}</td>
                        <td className="p-2.5 text-right text-slate-500">{r.weekly_hours || '—'}</td>
                        <td className="p-2.5 text-right font-semibold">{r.worked}</td>
                        <td className="p-2.5 text-right text-slate-500">{r.expected}</td>
                        <td className={`p-2.5 text-right font-semibold ${r.diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>{sign(r.diff)}</td>
                        <td className="p-2.5 text-right">{r.worked_days}</td>
                        <td className="p-2.5 text-right text-slate-500">{r.rest_days}</td>
                        <td className="p-2.5 text-right text-red-600">{r.holidays}</td>
                        <td className="p-2.5 text-right text-blue-600">{r.vacation_days}</td>
                        <td className="p-2.5 text-right text-purple-600">{r.free_days}</td>
                        <td className="p-2.5 text-right">{r.pending_requests > 0 ? <Badge className="bg-orange-100 text-orange-700 border-orange-300">{r.pending_requests}</Badge> : <span className="text-slate-300">0</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold">
                      <td className="p-2.5" colSpan={3}>TOTAL PLANTILLA ({report.totals.employees})</td>
                      <td className="p-2.5 text-right">{report.totals.worked}</td>
                      <td className="p-2.5 text-right">{report.totals.expected}</td>
                      <td className={`p-2.5 text-right ${report.totals.diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>{sign(report.totals.diff)}</td>
                      <td className="p-2.5 text-right">{report.totals.worked_days}</td>
                      <td className="p-2.5 text-right">{report.totals.rest_days}</td>
                      <td className="p-2.5 text-right">{report.totals.holidays}</td>
                      <td className="p-2.5 text-right">{report.totals.vacation_days}</td>
                      <td className="p-2.5 text-right">{report.totals.free_days}</td>
                      <td className="p-2.5 text-right">{report.totals.pending_requests}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
